import Keycloak from '@trimoz/keycloak-js';
import { getLocalParam, sanitizeDoubleSlashOnUrl } from './helpers';
import { DateTime } from 'luxon';
import { localStorageAvailable } from '../../utils';

const VUE_APP_KEYCLOAK_ACCESS_TOKEN_MIN_VALIDITY_SECONDS = 600;
export class KeycloakAdapter {
    constructor() {

        this.keycloak = null;

        this.isKcInstanceError = false;

        this.onRequestOnFulfilledFailCallback = () => null;

        this.realmAuth = process.env.VUE_APP_KEYCLOAK_REALM_AUTH;

        this.keycloakServerUrl = sanitizeDoubleSlashOnUrl(process.env.VUE_APP_KEYCLOAK_URL_AUTH);

        this.keycloakClientId = process.env.VUE_APP_KEYCLOAK_CLIENT_ID_AUTH;

        this.logoutUrlRedirect = sanitizeDoubleSlashOnUrl(window.location.href);

        this.registerUrlRedirect = sanitizeDoubleSlashOnUrl(window.location.href);

        this.isAdminRealm = /admin/i.test(process.env.VUE_APP_KEYCLOAK_REALM_AUTH);

        this.isProfileRealm = /profile/i.test(process.env.VUE_APP_KEYCLOAK_REALM_AUTH);

        this.keycloak = new Keycloak({
            realm: this.realmAuth,
            url: this.keycloakServerUrl,
            clientId: this.keycloakClientId,
        });

        this.keycloak.onAuthRefreshSuccess = () => {
            this.setAuthTsCookie();
        };

        this.keycloak.onAuthSuccess = () => {
            this.setAuthTsCookie();
        };

        this.keycloak.onAuthRefreshError = () => {
            this.deleteAuthTsCookie();
        };

        this.init = async(initOptions = {}) => {
            try {
                const decodedUrl = decodeURIComponent(window.location.href);
                const urlObj = new URL(decodedUrl);
                const urlParams = new URLSearchParams(urlObj.search);
                const localeOnUrl = urlParams.get('locale') || '';
                const localeLocalStorageKey = this.isAdminRealm
                    ? 'tmz_lang'
                    : 'tmz_public_lang';
                if (/^(fr|en)$/.test(localeOnUrl) && localStorageAvailable()) {
                    localStorage.setItem(localeLocalStorageKey, localeOnUrl);
                }

                const locale = localStorageAvailable()
                    ? localStorage.getItem(localeLocalStorageKey)
                    : '';
                const localParam = getLocalParam(new URL(window.location.href), locale)

                const options = {
                    onLoad: initOptions.onLoad || 'login-required',
                    checkLoginIframe: initOptions.checkLoginIframe || false,
                    redirectUri: sanitizeDoubleSlashOnUrl(initOptions.redirectUri || window.location.href) + localParam,
                };

                if (options.checkLoginIframe) {
                    options.silentCheckSsoRedirectUri = window.location.protocol + '//' + window.location.host + '/silent-check-sso.html';
                }

                const isUserAuth = await this.keycloak.init(options);
                if (isUserAuth) {
                    return this.keycloak.loadUserInfo();
                }

                return false;
            }
            catch (e) {
                console.log(e);
            }
        };

        this.requestOnFulfilled = async(config) => {
            if (this.isKcInstanceError ||
                (this.isProfileRealm && !this.validateCookieTs(document.cookie))) {
                return this.setPublicHeaders(config);
            }

            const validateOrRefreshToken = await this.isTokenValidOrRefresh();
            if (validateOrRefreshToken) {
                return this.setAuthHeaders(config);
            }
            this.onRequestOnFulfilledFailCallback();
            return this.setPublicHeaders(config);
        };

        this.requestOnRejected = (error) => {
            return error;
        };

        this.isTokenValidOrRefresh = async() => {
            if (!this.keycloak.token || !this.keycloak.authenticated) return false;
            if (this.isAuthenticated()) return true;
            await this.keycloak.updateToken(VUE_APP_KEYCLOAK_ACCESS_TOKEN_MIN_VALIDITY_SECONDS);
            return true;
        };

        this.isAuthenticated = () => {
            return this.keycloak.authenticated && !this.keycloak.isTokenExpired(VUE_APP_KEYCLOAK_ACCESS_TOKEN_MIN_VALIDITY_SECONDS);
        };

        this.setAuthHeaders = (config) => {
            if (!config.headers) config.headers = {};
            config.headers['X-TRIMOZ-ACCESS-TOKEN'] = 'Bearer ' + this.keycloak.token;
            config.headers['X-TRIMOZ-AUTH-REALM'] = this.realmAuth;
            return config;
        };

        this.setPublicHeaders = (config) => {
            if (!config.headers) config.headers = {};
            config.headers['X-TRIMOZ-ACCESS-TOKEN'] = 'public';
            return config;
        };

        this.login = () => {
            if (this.keycloak.authenticated) return;
            return this.keycloak.login();
        };

        this.logout = async(establishmentId = '') => {
            const locale = localStorageAvailable()
                ? localStorage.getItem(this.isAdminRealm ? 'tmz_lang' : 'tmz_public_lang')
                : '';
            this.deleteAuthTsCookie();
            await this.keycloak.logout({ redirectUri: this.logoutUrlRedirect + establishmentId + `${locale ? `?locale=${locale}` : ''}` });
            return true;
        };

        this.register = (establishmentId) => {
            if (this.keycloak.authenticated) return;
            return this.keycloak.register({ redirectUri: this.registerUrlRedirect + (establishmentId || '') });
        };

        this.createKeycloakServerUrl = ({ redirectUri, locale, isRegister }) => {
            const baseUrl = this.keycloakServerUrl + '/realms/' + this.realmAuth + '/protocol/openid-connect/';
            let url = baseUrl +
                'auth' +
                '?client_id=' + this.keycloakClientId +
                '&redirect_uri=' + redirectUri +
                '&response_type=code' +
                '&scope=openid' +
                '&ui_locales=' + locale;

            if (isRegister) {
                url += '&redirectToRegister=true';
            }
            return url;
        };

        this.isTokenTsValid = (tokenExpiresInTs) => {
            if (!tokenExpiresInTs || isNaN(+tokenExpiresInTs)) return false;
            return DateTime.fromSeconds(+tokenExpiresInTs) > DateTime.now();
        };

        this.convertTsToCookieTime = (ts) => {
            return DateTime.fromSeconds(ts).toUTC().toFormat('EEE, dd MMM yyyy HH:mm:ss \'UTC\'');
        };

        this.setAuthTsCookie = () => {
            if (this.isAdminRealm) return;
            if (!this.keycloak.refreshTokenParsed || !this.keycloak.refreshTokenParsed.exp) return;
            document.cookie = this.getCookieValue();
        };

        this.deleteAuthTsCookie = () => {
            if (this.isAdminRealm) return;
            document.cookie = this.getCookieValue(true);
        };

        this.hasCookieTs = (cookie) => {
            const cookies = cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();

                if (cookie.startsWith('reg_ts' + '=')) {
                    return cookie;
                }
            }
            return null;
        };

        this.validateCookieTs = (cookie) => {
            const tsCookie = this.hasCookieTs(cookie);
            if (tsCookie) {
                return this.isTokenTsValid(+tsCookie.substring('reg_ts'.length + 1));
            }
            return false;
        };

        this.getCookieValue = (isExpired = false) => {
            if (isExpired) {
                return `reg_ts=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${process.env.NODE_ENV === 'production' ? '.clicsante.ca' : 'localhost'}; path=/`;
            }

            if (!this.keycloak.refreshTokenParsed || !this.keycloak.refreshTokenParsed.exp) {
                return '';
            }

            return `reg_ts=${this.keycloak.refreshTokenParsed.exp}; expires=${this.convertTsToCookieTime(+this.keycloak.refreshTokenParsed.exp)}; domain=${process.env.NODE_ENV === 'production' ? '.clicsante.ca' : 'localhost'}; path=/`;
        };
    }
}
